import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, X, Eye, EyeOff, Lock, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_UID, validateAdminAccess } from '../config/adminConfig';


const FingerprintAdminAccess = ({ onAdminAccess, onClose }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { signIn } = useAuth();
  

  const handleLogin = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');

    try {
      const user = await signIn(email, password);
      
      // Use centralized admin validation
      if (!validateAdminAccess(user)) {
        setError('Unauthorized access. Only authorized admin accounts can access this panel.');
        return;
      }
      
      setShowLoginModal(false);
      setEmail('');
      setPassword('');
      if (onAdminAccess) {
        onAdminAccess();
      }
      
    } catch (error) {
      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No user found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials.';
      default:
        return 'Login failed. Please try again.';
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setError('');
    setShowPassword(false);
  };

  const handleModalClose = () => {
    setShowLoginModal(false);
    resetForm();
  };

  // Render very discrete component at Footer height
  return (
    <>
      {/* Admin Login Button - very small and transparent, barely visible */}
      <button
        onClick={() => setShowLoginModal(true)}
        className="w-6 h-6 bg-gray-900/20 backdrop-blur-sm rounded-full 
                   border border-gray-600/20 hover:border-solana-primary/40 
                   flex items-center justify-center text-gray-500
                   opacity-20 hover:opacity-60 transition-all duration-500 hover:scale-105"
        title="Admin Access"
        aria-label="Open admin panel"
      >
        <Fingerprint className="w-3 h-3" />
      </button>

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 admin-modal-overlay"
            onClick={handleModalClose}
            style={{ 
              zIndex: 2147483647,
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(15px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-900 rounded-2xl p-6 md:p-8 max-w-md w-full border border-gray-700 shadow-2xl admin-modal-container"
              onClick={(e) => e.stopPropagation()}
              style={{
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                zIndex: 2147483647,
                position: 'relative'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6 relative z-[100001]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-solana-primary/20 rounded-full flex items-center justify-center">
                    <Lock className="w-5 h-5 text-solana-primary" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-white">Admin Access</h2>
                </div>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-white transition-colors p-1 relative z-[100002]"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Login Form */}
              <form onSubmit={handleLogin} className="space-y-6 relative z-[100001]" onClick={(e) => e.stopPropagation()}>
                {/* Email Field */}
                <div className="relative z-[100001]">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-[100002]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-12 pr-4 py-3 text-white focus:border-solana-primary focus:outline-none transition-colors relative z-[100001]"
                      placeholder="Enter your email"
                      required
                      disabled={loading}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="relative z-[100001]">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-[100002]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-12 pr-12 py-3 text-white focus:border-solana-primary focus:outline-none transition-colors relative z-[100001]"
                      placeholder="Enter your password"
                      required
                      disabled={loading}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors z-[100002]"
                      disabled={loading}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-400 text-sm relative z-[100001]"
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full bg-gradient-to-r from-solana-primary to-neon-cyan text-white font-bold py-3 rounded-lg hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative z-[100001]"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Signing In...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FingerprintAdminAccess;